import { useMutation } from '@apollo/client';
import { logError } from 'fergy-core-react-logging';
import type {
	AddProductToProjectsMutation,
	AddProductToProjectsMutationVariables,
	AddToCartInput,
	ProjectInput,
	AddToCartPricedOption,
	AddCartToProjectInput,
	AddCartToProjectsMutation,
	AddCartToProjectsMutationVariables
} from '../../__generated__/graphql-client-types';
import { SaveToProjectSuccessBanner } from '../../components/product-components/save-to-projects-success-banner/save-to-project-success-banner.component';
import { PROJECTS_NOTIFICATION_TIMEOUT } from '../../constants/general';
import { ProjectSuccessActions } from '../../constants/project/general';
import {
	serializeComplexProductsSelectionToPricedOptions,
	serializeComplexProductsSelectionToSubItems,
	translatePricedOptionsToAddToCart
} from '../../helpers/product-helper/priced-options.helper';
import { ADD_CART_TO_PROJECTS } from '../../queries/cart/add-to-project.queries';
import { ADD_PRODUCT_TO_PROJECTS, GET_CURRENT_PROJECT } from '../../queries/project/project.queries';
import { type Selection } from '../../types/product-option.types';
import { type ProjectInputWithName } from '../../types/project.types';
import { useNotification } from '../notification/notification.hooks';

export const useAddProductToProjects = () => {
	const { notifyDanger, notifySuccess } = useNotification();
	const [AddProductToProjects, { loading: loadingAddToProjects }] = useMutation<
		AddProductToProjectsMutation,
		AddProductToProjectsMutationVariables
	>(ADD_PRODUCT_TO_PROJECTS);
	const [addCartToProjects] = useMutation<AddCartToProjectsMutation, AddCartToProjectsMutationVariables>(ADD_CART_TO_PROJECTS, {
		refetchQueries: [{ query: GET_CURRENT_PROJECT }]
	});

	const onError = () => {
		return notifyDanger('There was a problem updating your projects, please refresh and try again.', PROJECTS_NOTIFICATION_TIMEOUT);
	};

	const onSuccess = (projectInput: ProjectInputWithName[]) => {
		const affectedProjectIds = new Set(projectInput.map(({ projectId }) => projectId));
		const { projectId: id, name } = projectInput[0];
		notifySuccess(
			<SaveToProjectSuccessBanner
				action={ProjectSuccessActions.SAVE}
				project={affectedProjectIds.size === 1 ? { id, name } : undefined}
			/>,
			PROJECTS_NOTIFICATION_TIMEOUT
		);
	};

	const saveToProject = (
		projectInput: ProjectInputWithName[],
		variantId: number,
		pricedOptions?: AddToCartPricedOption[],
		subItems?: AddToCartInput[],
		ordinal?: number,
		isSavingCart?: boolean
	) => {
		const cleanedProjectInput = projectInput.map((project): ProjectInput => {
			return {
				cartId: project.cartId,
				projectId: project.projectId,
				quantity: ordinal || project.quantity,
				groupId: project.groupId
			};
		});
		const variables = {
			input: {
				projects: cleanedProjectInput,
				addToCartInput: {
					variantId,
					subItems,
					pricedOptions,
					ordinal
				},
				isSavingCart
			}
		};
		return AddProductToProjects({ variables })
			.then((res) => {
				if (res.errors) {
					return onError();
				}
				onSuccess(projectInput);
			})
			.catch((error) => {
				logError(error);
				onError();
			});
	};

	const saveCartToProject = (projectInput: ProjectInputWithName[]) => {
		const addCartToProjectInput: AddCartToProjectInput[] = projectInput.map((input) => ({
			groupId: input.groupId ?? 0,
			projectId: input.projectId,
			projectCartId: input.projectCartId
		}));
		addCartToProjects({ variables: { projectInputs: addCartToProjectInput } })
			.then((res) => {
				if (res.errors) {
					return onError();
				}
				onSuccess(projectInput);
			})
			.catch((error) => {
				logError(error);
				onError();
			});
	};

	const saveProductToProject = async (
		projectInput: ProjectInputWithName[],
		selection: Selection | undefined,
		selectedVariantId: number
	) => {
		const pricedOptions = serializeComplexProductsSelectionToPricedOptions(selection);
		const translatedPricedOptions = translatePricedOptionsToAddToCart(pricedOptions);
		const subItems = serializeComplexProductsSelectionToSubItems(selection);
		await saveToProject(projectInput, selectedVariantId, translatedPricedOptions, subItems);
	};

	return {
		saveToProject,
		saveCartToProject,
		saveProductToProject,
		loadingAddToProjects
	};
};
